import { LitElement, html, css } from "lit";
import { customElement } from "lit/decorators.js";
import { effect } from "@preact/signals-core";

import * as store from "../state/store.js";
import { render as paint, type Palette } from "./renderer.js";
import { setCanvasSize, centerOrigin } from "../ops/view.js";
import { attachInput } from "./input.js";
import { MM_PER_UNIT } from "../state/types.js";

// The <canvas> host. Owns the element, subscribes to store signals via
// effect(), and repaints whenever anything visible changes. Input handling is
// bolted on in `attachInput` — the view element doesn't know about ops.
@customElement("canvas-view")
export class CanvasView extends LitElement {
  static override styles = css`
    :host {
      display: block;
      position: relative;
      flex: 1;
      background: var(--canvas-bg);
      overflow: hidden;
    }

    canvas {
      display: block;
      width: 100%;
      height: 100%;
      /* Idle cursor. input.ts overrides to grabbing during pan; the drag-
         select cursor is also crosshair so no change is needed there. */
      cursor: crosshair;
    }

    .empty-state {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      pointer-events: none;
      text-align: center;
      color: var(--text-secondary);
    }

    /* The browser default [hidden] { display: none } is overridden by our
       display: flex above. Explicitly restore the hide behavior so the paint
       effect's empty.hidden = store.isLoaded() actually takes effect. */
    .empty-state[hidden] {
      display: none;
    }

    .empty-state .icon {
      font-size: 56px;
      margin-bottom: 12px;
    }
    .empty-state h2 {
      color: var(--text-primary);
      font-weight: 400;
      font-size: 20px;
      margin: 0 0 8px;
    }
    .empty-state p {
      font-size: 13px;
      margin: 0;
    }

    .selection-box {
      position: absolute;
      border: 1px dashed var(--selection-border);
      background: var(--selection-box);
      pointer-events: none;
      display: none;
    }
    .selection-box.visible {
      display: block;
    }
    .selection-box.outside {
      border-color: rgba(255, 100, 100, 0.8);
      background: rgba(255, 100, 100, 0.08);
    }

    .mode-badge {
      position: absolute;
      top: 12px;
      left: 50%;
      transform: translateX(-50%);
      background: var(--overlay-bg);
      color: var(--text-secondary);
      padding: 4px 14px;
      border-radius: 4px;
      font-size: 12px;
      border: 1px solid var(--border-color);
      pointer-events: none;
      display: none;
    }
    .mode-badge.visible {
      display: block;
    }
    .mode-badge strong {
      color: var(--text-primary);
      font-weight: 600;
    }

    /* Heal overlay: full-canvas scrim with a centered progress ring shown
       while /heal is in flight. pointer-events auto blocks pan/zoom/select
       so mid-heal edits can't collide with the state swap when the cleaned
       doc arrives. The raw (un-healed) DXF stays rendered beneath so the
       user can still see what file they loaded. */
    .heal-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 14px;
      background: var(--overlay-bg, rgba(0, 0, 0, 0.35));
      backdrop-filter: blur(2px);
      color: var(--text-primary);
      z-index: 5;
    }
    .heal-overlay[hidden] {
      display: none;
    }
    .heal-overlay .ring {
      width: 72px;
      height: 72px;
      color: var(--accent);
    }
    .heal-overlay .ring svg {
      width: 100%;
      height: 100%;
      /* Rotate the whole SVG so the arc starts at 12 o'clock. */
      transform: rotate(-90deg);
    }
    .heal-overlay .ring .track {
      stroke: var(--border-color);
    }
    /* Indeterminate spinner: fixed short arc, rotate infinitely. This is the
       heal case since /heal has no streaming checkpoints — we don't fake a
       percentage. Rotate the outer .ring div, not the SVG <circle>: SVG child
       transform-origin resolves inconsistently across browsers and can leave
       the arc looking frozen at ~25% instead of sweeping. !important
       overrides any user-agent or extension-injected reduce-motion — a
       progress indicator that stops progressing is worse UX than a small
       1Hz rotation. */
    .heal-overlay .ring.indeterminate {
      animation: heal-spin 1.2s linear infinite !important;
    }
    .heal-overlay .ring.indeterminate .fill {
      stroke: var(--accent);
      stroke-linecap: round;
    }
    @keyframes heal-spin {
      to {
        transform: rotate(360deg);
      }
    }
    .heal-overlay .label {
      font-size: 13px;
      letter-spacing: 0.04em;
      color: var(--text-secondary);
    }
  `;

  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private ro?: ResizeObserver;
  private disposeEffect?: () => void;
  private disposeHealEffect?: () => void;
  private detachInput?: () => void;
  private paletteCache: Palette | null = null;
  private hasCenteredOrigin = false;

  override render() {
    // Container + overlays. We don't render inside the <canvas>; it's driven
    // imperatively by the effect() subscription below. The heal overlay's
    // `hidden` attribute is toggled from an effect in firstUpdated (this
    // component uses plain LitElement, not SignalWatcher, so template reads
    // don't auto-track).
    return html`
      <canvas></canvas>
      <div class="empty-state" ?hidden=${this.isLoadedNow()}>
        <div class="icon">📐</div>
        <h2>DXF Layer Editor</h2>
        <p>Drop a DXF file here</p>
      </div>
      <div class="selection-box" id="selection-box"></div>
      <div class="mode-badge" id="mode-badge">
        <strong id="mode-badge-name">Inside</strong> selection
      </div>
      <div class="heal-overlay" id="heal-overlay" hidden>
        <div class="ring indeterminate">
          <svg viewBox="0 0 36 36">
            <circle
              class="track"
              cx="18"
              cy="18"
              r="15"
              fill="none"
              stroke-width="3"
            ></circle>
            <circle
              class="fill"
              cx="18"
              cy="18"
              r="15"
              fill="none"
              stroke-width="3"
              stroke-dasharray="24 94.2"
            ></circle>
          </svg>
        </div>
        <div class="label">Healing geometry…</div>
      </div>
    `;
  }

  private isLoadedNow(): boolean {
    return store.isLoaded();
  }

  override firstUpdated(): void {
    this.canvas = this.renderRoot.querySelector("canvas")!;
    const ctx = this.canvas.getContext("2d");
    if (!ctx) return;
    this.ctx = ctx;

    // Track resize via ResizeObserver so the canvas stays crisp on DPR changes.
    this.ro = new ResizeObserver(() => this.handleResize());
    this.ro.observe(this);
    this.handleResize();

    // Repaint whenever any relevant signal changes.
    this.disposeEffect = effect(() => {
      // Touch every read we care about so signals-core tracks them.
      store.entities();
      store.layers();
      store.layerOrder();
      store.selectedEntityIds();
      store.zoom();
      store.pan();
      store.gridSpacing();
      store.gridUnit();
      store.dxfUnit();
      store.showGrid();
      store.showGridRuler();
      store.theme();
      store.viewSeq();
      // Schedule the actual paint on the next frame so batched writes coalesce.
      queueMicrotask(() => this.paint());
    });

    this.detachInput = attachInput(this, this.canvas);

    // Toggle the heal overlay's `hidden` attr whenever store.healActive
    // flips. Kept imperative (not a template read) because this component
    // uses plain LitElement — template renders don't auto-subscribe.
    this.disposeHealEffect = effect(() => {
      const active = store.healActive();
      const overlay = this.renderRoot.querySelector<HTMLDivElement>("#heal-overlay");
      if (overlay) overlay.hidden = !active;
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.ro?.disconnect();
    this.disposeEffect?.();
    this.disposeHealEffect?.();
    this.detachInput?.();
  }

  private handleResize(): void {
    const rect = this.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = Math.max(1, Math.round(rect.width * dpr));
    this.canvas.height = Math.max(1, Math.round(rect.height * dpr));
    this.canvas.style.width = `${rect.width}px`;
    this.canvas.style.height = `${rect.height}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    setCanvasSize(rect.width, rect.height);

    // On the very first sized layout with no DXF loaded, put the origin at
    // canvas center. Subsequent resizes leave the user's pan alone.
    if (
      !this.hasCenteredOrigin &&
      !store.isLoaded() &&
      rect.width > 0 &&
      rect.height > 0
    ) {
      this.hasCenteredOrigin = true;
      centerOrigin();
    }

    this.paint();
  }

  private palette(): Palette {
    // Re-read on theme swap. Cheap enough to just recompute every paint, but
    // we cache to avoid a getComputedStyle call every frame during panning.
    if (this.paletteCache) return this.paletteCache;
    const cs = getComputedStyle(this);
    const grab = (name: string) => cs.getPropertyValue(name).trim();
    this.paletteCache = {
      grid: grab("--grid-color") || "rgba(128,128,128,0.15)",
      gridMajor: grab("--grid-major") || "rgba(128,128,128,0.3)",
      primary: grab("--text-primary") || "#fff",
      rulerBg: grab("--ruler-bg") || "rgba(30,30,30,0.85)",
      rulerTick: grab("--ruler-tick") || "rgba(255,255,255,0.5)",
      rulerLabel: grab("--ruler-label") || "rgba(255,255,255,0.85)",
      rulerBorder: grab("--ruler-border") || "rgba(255,255,255,0.15)",
    };
    return this.paletteCache;
  }

  private paint(): void {
    if (!this.ctx || !this.canvas) return;
    const rect = this.canvas.getBoundingClientRect();
    // If theme changed the palette shifts, so invalidate on every theme write.
    this.paletteCache = null;
    // Grid spacing is authored in `gridUnit` but the renderer works in world
    // coordinates (`dxfUnit`), so we convert here at the boundary. A "1 in"
    // grid on a millimetre-authored drawing draws lines every 25.4 world
    // units; the ruler labels themselves still read "1 in".
    const gridSpacing = store.gridSpacing();
    const gridUnit = store.gridUnit();
    const dxfUnit = store.dxfUnit();
    const worldFactor = MM_PER_UNIT[gridUnit] / MM_PER_UNIT[dxfUnit];
    paint({
      ctx: this.ctx,
      width: rect.width,
      height: rect.height,
      zoom: store.zoom(),
      pan: store.pan(),
      gridSpacing,
      gridStepWorld: gridSpacing * worldFactor,
      gridUnit,
      showGrid: store.showGrid(),
      showGridRuler: store.showGridRuler(),
      entities: store.entities(),
      layers: store.layers(),
      layerOrder: store.layerOrder(),
      selectedIds: store.selectedEntityIds(),
      palette: this.palette(),
    });

    // Empty-state visibility follows isLoaded — re-request a Lit render
    // whenever that changes.
    const empty = this.renderRoot.querySelector<HTMLDivElement>(".empty-state");
    if (empty) empty.hidden = store.isLoaded();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "canvas-view": CanvasView;
  }
}
